import Swal from "sweetalert2";

export const escapeHtml = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// "About the data" info modal for the vehicle-information clause (§1) — shared
// by the seller review, buyer review and final contract pages. Explains which
// fields come from the official registry (via Bilvision) vs. the seller.
export function showVehicleSourceInfo(t, fetchedAt) {
  const fetchedText = fetchedAt
    ? new Date(fetchedAt).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-source">
        <p class="ds-swal-price-title">${escapeHtml(t("agreement.sourceModalTitle"))}</p>
        <h3 class="ds-swal-source-heading">${escapeHtml(t("agreement.sourceModalVerifiedTitle"))}</h3>
        <p class="ds-swal-price-desc">${escapeHtml(t("agreement.sourceModalVerifiedBody"))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t("agreement.sourceModalUnavailableNote"))}</p>
        ${fetchedText ? `<p class="ds-swal-price-note">${escapeHtml(t("agreement.sourceModalFetched"))}: ${escapeHtml(fetchedText)}</p>` : ""}
        <h3 class="ds-swal-source-heading">${escapeHtml(t("agreement.sourceModalSellerTitle"))}</h3>
        <p class="ds-swal-price-desc">${escapeHtml(t("agreement.sourceModalSellerBody"))}</p>
        <p class="ds-swal-price-subtext">${escapeHtml(t("agreement.sourceModalLegal"))}</p>
        <h3 class="ds-swal-source-heading">${escapeHtml(t("agreement.sourceModalAboutBilvisionTitle"))}</h3>
        <p class="ds-swal-price-desc">${escapeHtml(t("agreement.sourceModalAboutBilvisionBody"))}</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: t("agreement.close") || "Close",
    buttonsStyling: false,
    customClass: { popup: "ds-swal-popup ds-swal-source-popup", htmlContainer: "ds-swal-price-container", confirmButton: "ds-swal-price-save" },
  });
}

// "Leave the deal?" confirmation used by both buyer flows (manual join and QR
// landing). Same custom-html SweetAlert2 layout as the seller's change-price /
// wrong-buyer dialogs. Resolves true only when the buyer confirms.
export async function confirmLeaveDeal(t) {
  const result = await Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-price-confirm">
        <p class="ds-swal-price-title">${escapeHtml(t("buyer.confirmLeave.title"))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t("buyer.confirmLeave.body"))}</p>
        <div class="ds-swal-price-actions">
          <button type="button" class="ds-swal-price-cancel" id="ds-swal-leave-cancel">${escapeHtml(t("buyer.confirmLeave.cancel"))}</button>
          <button type="button" class="ds-swal-price-disconnect" id="ds-swal-leave-confirm">${escapeHtml(t("buyer.confirmLeave.confirm"))}</button>
        </div>
      </div>
    `,
    showConfirmButton: false,
    showCancelButton: false,
    buttonsStyling: false,
    customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
    didOpen: (popup) => {
      popup.querySelector("#ds-swal-leave-cancel").addEventListener("click", () => Swal.close());
      popup.querySelector("#ds-swal-leave-confirm").addEventListener("click", () => Swal.close({ isConfirmed: true }));
    },
  });
  return !!result.isConfirmed;
}

// Shown to the party who did NOT time out, once their counterpart's session
// ends via SessionTimeoutManager (idle timeout or the absolute session
// limit) — see the "<role> session timed out" SignalR message listener in
// SellerProfileFlow.jsx / BuyerProfileFlow.jsx. Same custom-html layout as
// confirmLeaveDeal. "Wait" just closes (the deal stays open server-side —
// the timed-out party can resume it by re-logging in); "Disconnect"
// resolves true so the caller runs its own existing disconnect flow.
export async function confirmWaitOrDisconnect(t, counterpartRoleKey) {
  const result = await Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-price-confirm">
        <p class="ds-swal-price-title">${escapeHtml(t("dealTimeout.title"))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t(`dealTimeout.body.${counterpartRoleKey}`))}</p>
        <div class="ds-swal-price-actions">
          <button type="button" class="ds-swal-price-cancel" id="ds-swal-timeout-wait">${escapeHtml(t("dealTimeout.wait"))}</button>
          <button type="button" class="ds-swal-price-disconnect" id="ds-swal-timeout-disconnect">${escapeHtml(t("dealTimeout.disconnect"))}</button>
        </div>
      </div>
    `,
    showConfirmButton: false,
    showCancelButton: false,
    buttonsStyling: false,
    customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
    didOpen: (popup) => {
      popup.querySelector("#ds-swal-timeout-wait").addEventListener("click", () => Swal.close());
      popup.querySelector("#ds-swal-timeout-disconnect").addEventListener("click", () => Swal.close({ isConfirmed: true }));
    },
  });
  return !!result.isConfirmed;
}

// The facts every "this deal is over" notice carries: which vehicle the deal was about
// and who the two parties were. Shared by the expiry notice and the seller-cancelled
// notice so both read identically. The labels live under dealExpired.* — plain field
// names ("Registration number", "Vehicle", "Seller", "Buyer") already translated in
// both languages — and are reused rather than duplicated under dealSession.
// Each row renders only when its value is present, so a deal missing a party (a buyer
// who never connected) simply shows fewer rows instead of an empty one.
const dealFactsBlock = (t, deal = {}) => {
  const row = (label, value) =>
    value
      ? `<div style="display:flex;justify-content:space-between;gap:16px;padding:8px 0;border-top:1px solid rgba(20,36,46,.08);font-size:14px;"><span style="color:#55616b;">${escapeHtml(label)}</span><strong style="color:#14242e;text-align:right;">${escapeHtml(value)}</strong></div>`
      : "";
  const vehicle = [deal.carInfoVehicleDesignation, deal.carInfoTradeName].filter(Boolean).join(" ");
  const rows = [
    row(t("dealExpired.registration"), deal.carInfoRegistrationNo),
    row(t("dealExpired.vehicle"), vehicle),
    row(t("dealExpired.seller"), deal.sellerName),
    row(t("dealExpired.buyer"), deal.buyerName),
  ].join("");
  if (!rows) return "";
  return `<div style="margin:4px 0 14px;text-align:left;">${rows}</div>`;
};

// "This deal has expired" notice shown to BOTH parties (live, while connected, or once at next
// login): the vehicle and the two parties, the reason and a "Go to home page" button.
// Resolves when the user presses the button.
export function showDealExpiredDialog(t, deal = {}) {
  return Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-price-confirm">
        <p class="ds-swal-price-title">${escapeHtml(t("dealExpired.title"))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t("dealExpired.body"))}</p>
        ${dealFactsBlock(t, deal)}
        <p class="ds-swal-price-desc">${escapeHtml(t("dealExpired.cancelled"))}</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: t("dealExpired.home"),
    buttonsStyling: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container", confirmButton: "ds-swal-price-save" },
  });
}

// Clicking "View the agreement" on the transaction record of a deal that never produced a
// signed agreement: say why there is nothing to open instead of presenting an empty or
// draft contract as if it were the real one. `status` is the deal state the record page
// already derives ("cancelled" | "expired" | "in_progress"); anything unrecognised is
// treated as still in progress, which is the harmless reading.
// Resolves when the user closes it.
export function showContractUnavailableDialog(t, status) {
  const key = status === "cancelled" || status === "expired" ? status : "inProgress";
  return Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-price-confirm">
        <p class="ds-swal-price-title">${escapeHtml(t(`ds.record.contract.unavailable.${key}.title`))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t(`ds.record.contract.unavailable.${key}.body`))}</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: t("ds.record.contract.unavailable.close"),
    buttonsStyling: false,
    customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container", confirmButton: "ds-swal-price-save" },
  });
}

// "The <party> cancelled the deal" notice — shown live (this user is in the deal), or once on
// their next login / while signed in at home (they were not in the deal when it was cancelled).
// Carries the same vehicle-and-parties block as the expiry notice above, so they can see WHICH
// deal was cancelled instead of only being told that one was.
// `deal` is optional: with nothing to show it degrades to the message alone, as before.
// `cancelledBy` names the party who did it — either may, from the "Leave the deal?" dialog. It
// defaults to "seller", which is right for the login-time notice: that one only ever reaches the
// buyer (see GetExpiredDealNotice), so the canceller there was necessarily the seller.
// Resolves when the user has dismissed it.
export function showSellerCancelledDialog(t, deal = {}, cancelledBy = "seller") {
  const key = cancelledBy === "buyer" ? "dealSession.otherCancelledByBuyer" : "dealSession.otherCancelled";
  return Swal.fire({
    html: `
      <div class="ds-swal-price ds-swal-price-confirm">
        <p class="ds-swal-price-title">${escapeHtml(t(`${key}Title`))}</p>
        <p class="ds-swal-price-desc">${escapeHtml(t(`${key}Body`))}</p>
        ${dealFactsBlock(t, deal)}
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: t("dealSession.ok"),
    buttonsStyling: false,
    allowOutsideClick: false,
    customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container", confirmButton: "ds-swal-price-save" },
  });
}
